import React, { createContext, useState, useEffect } from "react";
import AuthServices from "../services/auth/AuthServices";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLogged, setIsLogged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [alertMessage, setAlertMessage] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const publicRoutes = ["/", "/login", "/forgot-password"];
  const isResetPasswordRoute = location.pathname.startsWith("/reset-password/");
  const isPublicRoute =
    publicRoutes.includes(location.pathname) || isResetPasswordRoute;

  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const res = await AuthServices.login(credentials);
      const userData = res.data.data.user;

      if (userData.active === 0) {
        setAlertMessage({
          type: "danger",
          text: "Votre compte est désactivé. Veuillez contacter l'administrateur.",
        });
        toast.error(
          "Votre compte est désactivé. Veuillez contacter l'administrateur.",
          {
            position: "top-right",
            theme: "colored",
            className: "toast-message",
          },
        );
        return;
      }

      setUser(userData);
      setIsLogged(true);
      localStorage.setItem("access_token", res.data.token);
      localStorage.setItem("user", JSON.stringify(userData));

      if (userData.role === 1) {
        navigate("/users");
      } else if (userData.role === 2) {
        navigate("/surveillance");
      } else {
        navigate("/login");
      }
    } catch (err) {
      console.error("Erreur de connexion :", err.response?.data?.message);
      setAlertMessage({
        type: "danger",
        text: err.response?.data?.message || "Erreur de connexion !",
      });
      toast.error(err.response?.data?.message || "Erreur inconnue", {
        position: "top-right",
        theme: "colored",
        className: "toast-message",
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    AuthServices.logout()
      .then(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        setIsLogged(false);
        setUser(null);
        navigate("/login");
      })
      .catch((err) => {
        console.error("Erreur de déconnexion :", err);
      });
  };

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => setAlertMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  // 1) Vérification du token — une seule fois au montage de l'app.
  //    Ne dépend plus de location.pathname : ne se relance jamais lors d'une navigation interne.
  useEffect(() => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      setIsLogged(false);
      setUser(null);
      setIsAuthChecking(false);
      return;
    }

    AuthServices.checkToken(token)
      .then((res) => {
        const userData = res.data.data.user;

        if (!userData) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user");
          setIsLogged(false);
          setUser(null);
          return;
        }

        if (userData.active === 0) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user");
          setIsLogged(false);
          setUser(null);
          setAlertMessage({
            type: "danger",
            text: "Votre compte est désactivé. Veuillez contacter l'administrateur.",
          });
          toast.error(
            "Votre compte est désactivé. Veuillez contacter l'administrateur.",
            {
              position: "top-right",
              theme: "colored",
              className: "toast-message",
            },
          );
          return;
        }

        setUser(userData);
        setIsLogged(true);
      })
      .catch(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        setIsLogged(false);
        setUser(null);
      })
      .finally(() => {
        setIsAuthChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← une seule fois, au montage

  // 2) Redirections selon l'état d'auth — peut réagir à la navigation,
  //    mais ne touche jamais à isAuthChecking, donc ne démonte plus Sidebar/Topbar.
  useEffect(() => {
    if (isAuthChecking) return; // attend la fin de la vérification initiale

    if (!isLogged) {
      if (!isPublicRoute) {
        navigate("/login", { replace: true });
      }
      return;
    }

    // Déjà connecté : on ne doit pas rester sur /login, /forgot-password, /reset-password/:token
    if (
      location.pathname === "/login" ||
      location.pathname === "/forgot-password" ||
      isResetPasswordRoute
    ) {
      if (user?.role === 1) {
        navigate("/users", { replace: true });
      } else if (user?.role === 2) {
        navigate("/surveillance", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthChecking, isLogged, location.pathname]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLogged,
        isLoading,
        isAuthChecking,
        login,
        logout,
        setIsLoading,
        alertMessage,
        setAlertMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
